import type { Connection } from "@/lib/types";
import { completeOwnedConnections } from "@/lib/data/requests/connections/owned-connections";
import type {
  SurveySection,
  SurveyDraftSection,
} from "../../../../../shared/survey-questions";
import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import { type APIResponse } from "@/lib/types/APIResponse";

export type MatterSurveyQuestionType =
  | "short_answer"
  | "long_answer"
  | "multiple_choice"
  | "checkboxes"
  | "date"
  | "time"
  | "datetime";

export interface MatterSurveyQuestionDefinition {
  sectionId?: string | null;
  displayWhen?: { questionId: string; optionIds: string[] } | null;
  prompt: string;
  type: MatterSurveyQuestionType;
  required: boolean;
  options: Array<{ id: string; label: string }>;
}

export interface MatterSurvey {
  id: string;
  firmId: string;
  matterId: string;
  recipientConnectionId: string;
  connections: Connection[];
  connectionsNextCursor?: string | null;
  title: string;
  description: string | null;
  sourceDefaultId: string | null;
  expiresAt: string;
  submittedAt: string | null;
  cancelledAt: string | null;
  dateCreated: string;
  status:
    | "pending"
    | "in_progress"
    | "reopened"
    | "completed"
    | "expired"
    | "cancelled";
  roundId: string | null;
  readOnly: boolean;
  latestSubmissionId: string | null;
  latestSubmissionVersion: number | null;
}

export interface MatterSurveyQuestion extends MatterSurveyQuestionDefinition {
  id: string;
  matterSurveyId: string;
  position: number;
}

export interface MatterSurveyAnswerReview {
  sections?: SurveySection[];
  surveyId: string;
  submittedAt: string | null;
  acknowledgement: {
    available: boolean;
    acknowledgementFullName: string | null;
    acknowledgedAt: string | null;
    acknowledgementText: string | null;
  };
  questions: Array<
    MatterSurveyQuestion & {
      answer: {
        id: string;
        questionId: string;
        value: string | string[];
      } | null;
    }
  >;
}

export interface MatterSurveyFillQuestion
  extends MatterSurveyQuestionDefinition {
  id: string;
  position: number;
  answer: string | string[] | null;
  prefillValue: string | string[] | null;
}

export interface MatterSurveyAnswerSession {
  sections?: SurveySection[];
  id: string;
  title: string;
  description: string | null;
  expiresAt: string;
  submittedAt: string | null;
  readOnly: boolean;
  draftAnswers: Record<string, string | string[] | null>;
  draftRevision: number;
  roundId: string | null;
  status: MatterSurvey["status"];
  recipient: { name: string };
  unavailableReason: "cancelled" | "expired" | null;
  questions: MatterSurveyFillQuestion[];
}

export interface MatterSurveyAnswerPayload {
  surveyId: string;
  answers: Array<{ questionId: string; value: string | string[] | null }>;
  draftRevision: number;
  roundId: string | null;
}

export interface MatterSurveyAcknowledgementPayload {
  acknowledgementFullName: string;
  acknowledgement: true;
  acknowledgedAt: string;
}

export interface ReopenMatterSurveyPayload {
  surveyId: string;
  expectedSubmissionId: string;
  expectedSubmissionVersion: number;
  expiresAt?: string;
}

export interface CreateMatterSurveyPayload {
  sections?: SurveyDraftSection[];
  matterId: string;
  title: string;
  description?: string | null;
  recipientConnectionId: string;
  expiresAt: string;
  questions: (MatterSurveyQuestionDefinition & { key?: string; id?: string })[];
}

export type MatterSurveyDeliveryChannel = "email" | "sms";

export interface MatterSurveyDeliveryAttempt {
  channel: MatterSurveyDeliveryChannel;
  status: "accepted" | "failed";
  message: string;
}

export interface MatterSurveyDelivery {
  surveyId: string;
  attempts: MatterSurveyDeliveryAttempt[];
}

export interface DeliverMatterSurveyPayload {
  surveyId: string;
  channels: MatterSurveyDeliveryChannel[];
  firmPhoneId?: string | null;
}

const surveysRoute = "matters/v4/surveys";
const conditionalSurveysRoute = surveysRoute.replace("/v2/", "/v4/");

export function toSurveyQuestionInputs(
  questions: (MatterSurveyQuestionDefinition & { key?: string; id?: string })[],
  keepIds = false,
) {
  return questions.map((question) => ({
    key: question.key ?? question.id ?? crypto.randomUUID(),
    ...(keepIds && question.id ? { id: question.id } : {}),
    sectionKey: question.sectionId ?? null,
    prompt: question.prompt.trim(),
    type: question.type,
    required: question.required,
    options: question.options,
    displayWhen: question.displayWhen
      ? {
          questionKey: question.displayWhen.questionId,
          optionIds: question.displayWhen.optionIds,
        }
      : null,
  }));
}

export const listMatterSurveys = (matterId: string) =>
  authenticatedFastApiAxiosInstance
    .get<APIResponse<MatterSurvey[]>>(surveysRoute, {
      params: { matterId, pageNumber: 1, pageSize: 500 },
    })
    .then(async (res) => ({
      ...res.data,
      data: await Promise.all(
        res.data.data.map((survey) =>
          completeOwnedConnections(
            survey,
            `${surveysRoute}/${survey.id}`,
            matterId,
          ),
        ),
      ),
    }));

export const createMatterSurvey = (payload: CreateMatterSurveyPayload) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MatterSurvey>
    >(conditionalSurveysRoute, { ...payload, sections: toSurveySectionInputs(payload.sections ?? []), questions: toSurveyQuestionInputs(payload.questions) })
    .then((res) => res.data);

export const cancelMatterSurvey = (surveyId: string) =>
  authenticatedFastApiAxiosInstance
    .post<APIResponse<MatterSurvey>>(`${surveysRoute}/${surveyId}/cancel`)
    .then((res) => res.data);

export const deleteMatterSurvey = (surveyId: string) =>
  authenticatedFastApiAxiosInstance
    .delete<APIResponse<MatterSurvey>>(`${surveysRoute}/${surveyId}`)
    .then((res) => res.data);

export const retrieveMatterSurvey = (surveyId: string, matterId: string) =>
  authenticatedFastApiAxiosInstance
    .get<APIResponse<MatterSurvey>>(`${surveysRoute}/${surveyId}`, {
      params: { matterId },
    })
    .then(async (res) => ({
      ...res.data,
      data: await completeOwnedConnections(
        res.data.data,
        `${surveysRoute}/${surveyId}`,
        matterId,
      ),
    }));

export const retrieveMatterSurveyQuestions = (surveyId: string) =>
  authenticatedFastApiAxiosInstance
    .get<
      APIResponse<MatterSurveyQuestion[]>
    >(`${conditionalSurveysRoute}/${surveyId}/questions`)
    .then((res) => res.data);

export const retrieveMatterSurveyAnswers = (surveyId: string) =>
  authenticatedFastApiAxiosInstance
    .get<
      APIResponse<MatterSurveyAnswerReview>
    >(`${conditionalSurveysRoute}/${surveyId}/answers`)
    .then((res) => res.data);

export const retrieveMatterSurveyAnswerSession = (surveyId: string) =>
  authenticatedFastApiAxiosInstance
    .get<
      APIResponse<MatterSurveyAnswerSession>
    >(`${conditionalSurveysRoute}/${surveyId}/answer-session`)
    .then((res) => res.data);

export const saveMatterSurveyDraft = ({
  surveyId,
  ...payload
}: MatterSurveyAnswerPayload) =>
  authenticatedFastApiAxiosInstance
    .patch<
      APIResponse<{
        surveyId: string;
        draftAnswers: Record<string, string | string[] | null>;
        draftRevision: number;
      }>
    >(`${conditionalSurveysRoute}/${surveyId}/draft`, payload)
    .then((res) => res.data);

export const submitMatterSurveyOnBehalf = ({
  surveyId,
  ...payload
}: MatterSurveyAnswerPayload & MatterSurveyAcknowledgementPayload) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MatterSurveyAnswerReview>
    >(`${conditionalSurveysRoute}/${surveyId}/submission`, payload)
    .then((res) => res.data);

export const reopenMatterSurvey = ({
  surveyId,
  ...payload
}: ReopenMatterSurveyPayload) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MatterSurveyAnswerSession>
    >(`${conditionalSurveysRoute}/${surveyId}/reopen`, payload)
    .then((res) => res.data);

export const deliverMatterSurvey = ({
  surveyId,
  ...payload
}: DeliverMatterSurveyPayload) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MatterSurveyDelivery>
    >(`${surveysRoute}/${surveyId}/deliveries`, payload)
    .then((res) => res.data);

export function toSurveySectionInputs(
  sections: SurveyDraftSection[],
  keepIds = false,
) {
  return sections.map((section) => ({
    key: section.key,
    ...(keepIds && section.persisted ? { id: section.id } : {}),
    title: section.title.trim(),
    description: section.description?.trim() || null,
  }));
}
